import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Button, 
  Text, 
  useToast, 
  Box, 
  Image as ChakraImage, 
  VStack, 
  HStack, 
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
} from '@chakra-ui/react';
import { Image, X, CaretDown, Plus, Images } from '@phosphor-icons/react';
import CommentImagePreviewModal from './CommentImagePreviewModal';
import CommentImageSelectModal from './CommentImageSelectModal';
import { uploadFileToR2 } from '../services/r2Service';

const ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp']
};

export default function CommentImageUpload({ onUploadComplete, siteId, previewSize = "100px", initialUrl = null }) {
  const [preview, setPreview] = useState(initialUrl);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const toast = useToast();

  // Update preview when initialUrl changes
  useEffect(() => {
    console.log('Initial URL changed:', initialUrl);
    setPreview(initialUrl);
  }, [initialUrl]);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    try {
      const file = acceptedFiles[0];
      
      // Upload to R2 via worker
      const uploadResult = await uploadFileToR2(file);
      const downloadURL = uploadResult.url;
      
      setPreview(downloadURL);
      onUploadComplete(downloadURL);
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [onUploadComplete, toast, siteId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024 // 5MB
  });

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setPreview(null);
    onUploadComplete('');
  };

  const handleSelectImage = useCallback((url) => {
    console.log('Selected URL:', url);
    if (url) {
      setPreview(url);
      onUploadComplete(url);
    }
  }, [onUploadComplete]);

  useEffect(() => {
    console.log('Current preview:', preview);
  }, [preview]);

  return (
    <VStack align="start" spacing={2}>
      <Menu>
        <MenuButton as={Button} rightIcon={<CaretDown />} size="sm" variant="outline">
          <HStack spacing={2}>
            <Image />
            <Text>Add Image</Text>
          </HStack>
        </MenuButton>
        <Portal>
          <MenuList>
            <MenuItem icon={<Plus />} {...getRootProps()}>
              <input {...getInputProps()} />
              Upload New
            </MenuItem>
            <MenuItem 
              icon={<Images />} 
              onClick={() => setIsSelectModalOpen(true)}
            >
              Select from Site Media
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>

      {preview && (
        <HStack spacing={2} align="start">
          <Box 
            position="relative" 
            cursor="pointer" 
            onClick={() => setIsPreviewModalOpen(true)}
          >
            <ChakraImage
              src={preview}
              alt="Preview"
              boxSize={previewSize}
              objectFit="cover"
              borderRadius="md"
              border="1px solid"
              borderColor="gray.200"
            />
          </Box>
          <IconButton
            icon={<X />}
            size="xs"
            aria-label="Remove image"
            onClick={handleRemoveImage}
          />
        </HStack>
      )}

      <CommentImagePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        imageUrl={preview}
      />

      <CommentImageSelectModal 
        isOpen={isSelectModalOpen}
        onClose={() => setIsSelectModalOpen(false)}
        siteId={siteId}
        onSelect={handleSelectImage}
      />
    </VStack>
  );
}
